/**
 * Wallet data hooks.
 *
 * PEL-823 introduced `useWalletBalance` for the Home screen chip.
 * PEL-834 PR A extends the surface with transaction history +
 * auto-reload mutation so the full Wallet screen can render.
 *
 * Cache keys:
 *   ['wallet']                          — base (invalidate-all)
 *   ['wallet', 'balance']               — current balance + auto-reload config
 *   ['wallet', 'transactions', page]    — paginated transaction list
 */

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type {
  AddFundsRequest,
  AutoReloadConfig,
  WalletBalance,
  WalletTransactionsResponse,
} from '@pelipost/consumer-api-client';
import { api } from '../lib/api/client';

export const walletKeys = {
  all: ['wallet'] as const,
  balance: () => ['wallet', 'balance'] as const,
  transactions: (page: number) => ['wallet', 'transactions', page] as const,
};

/** Current wallet balance + auto-reload config. */
export function useWalletBalance(
  options?: Omit<UseQueryOptions<WalletBalance>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<WalletBalance>({
    queryKey: walletKeys.balance(),
    queryFn: () => api.wallet.getBalance(),
    ...options,
  });
}

/** Paginated wallet transactions (credits + debits). */
export function useWalletTransactions(
  page: number = 1,
  options?: Omit<
    UseQueryOptions<WalletTransactionsResponse>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<WalletTransactionsResponse>({
    queryKey: walletKeys.transactions(page),
    queryFn: () => api.wallet.getTransactions(page),
    ...options,
  });
}

/**
 * Add funds to the wallet via an existing / freshly tokenized payment method.
 *
 * Invalidates the base `wallet` key on success so the balance hero, TopBar
 * chip, AND transaction list all refresh to reflect the new credit.
 */
export function useAddFunds() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, AddFundsRequest>({
    mutationFn: (payload) => api.wallet.addFunds(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: walletKeys.all });
    },
  });
}

/**
 * Toggle / configure auto-reload.
 *
 * On success we invalidate the base `wallet` key so both the balance card
 * and the Home TopBar chip re-read fresh data.
 */
export function useUpdateAutoReload() {
  const qc = useQueryClient();
  return useMutation<WalletBalance, Error, AutoReloadConfig>({
    mutationFn: (config) => api.wallet.updateAutoReload(config),
    onSuccess: (updated) => {
      // Write-through for snappier UI—the balance card won't flicker while
      // the refetch round-trips.
      qc.setQueryData(walletKeys.balance(), updated);
      qc.invalidateQueries({ queryKey: walletKeys.all });
    },
  });
}
